import { useEffect, useState } from 'react';
import {
  Alert,
  Link,
  Button,
  formFieldRequiredLabel as requiredLabel,
  TextField,
} from '@elementary/common-ui/components';
import { alertIcons } from '@elementary/common-ui/components/Alert/Alert';
import FormControls from '@elementary/common-ui/components/FormControls';
import { Autocomplete, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import MissingChannelHelper from 'shared/components/SlackIntegration/MissingChannelHelper';
import ParietalChannelListAlert from 'shared/components/SlackIntegration/ParietalChannelListAlert';
import useNango from 'shared/hooks/useNango';
import useSupportChat from 'shared/hooks/useSupportChat';
import {
  FetchSlackApi,
  SetSlackIntegrationResponse,
  SlackForm,
} from 'shared/types';
import { useSlackIntegration } from 'shared/hooks/integrations';
import { IntegrationError } from '../../../types';
import IntegrationDialog from '../../shared/Integration/IntegrationDialog';
import EditConfirmationDialog from './EditConfirmationDialog';
import TestIntegrationSection from './IntegrationTestSections/TestIntegrationSection';
import SlackTestSection from './IntegrationTestSections/SlackTestSection';

type Props = {
  open: boolean;
  onClose: () => void;
  data?: FetchSlackApi;
  onSubmit: (values: SlackForm) => void;
  onSlackIntegrationSubmit: () => void;
  slackIntegrationResponse?: SetSlackIntegrationResponse;
  error?: IntegrationError;
  slackIntegrationError?: IntegrationError;
  loading?: boolean;
  envId: string;
  appInstalled?: boolean;
  edit?: boolean;
  onDisconnect?: () => void;
};

const SlackDialog = ({
  open,
  onClose,
  data,
  onSubmit,
  onSlackIntegrationSubmit,
  slackIntegrationResponse,
  error,
  slackIntegrationError,
  loading,
  envId,
  appInstalled,
  edit = false,
  onDisconnect,
}: Props) => {
  const initialChannelName = data?.defaultDestination?.channelName ?? '';

  const [oauthOpened, setOauthOpened] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [showEditConfirmationDialog, setShowEditConfirmationDialog] =
    useState(false);
  const { nangoAuth, success: nangoSuccess, error: nangoError } = useNango();
  const supportChat = useSupportChat();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<SlackForm>({
    defaultValues: {
      channelName: initialChannelName,
    },
  });

  const { slackChannelsMutation } = useSlackIntegration({ envId });

  const {
    mutate: getSlackChannels,
    error: slackError,
    data: slackData,
    isLoading: slackChannelsLoading,
  } = slackChannelsMutation;

  const showDeprecationWarning =
    !appInstalled &&
    data?.integration === 'token' &&
    !nangoError &&
    !nangoSuccess;

  useEffect(() => {
    if (nangoSuccess) {
      onSlackIntegrationSubmit();
      // It needs the alert integration only for backwards compatibility, so we don't need to await for the integration submit.
      getSlackChannels();
    }
  }, [getSlackChannels, onSlackIntegrationSubmit, nangoSuccess]);

  useEffect(() => {
    if (!open) {
      return;
    }

    if (!appInstalled && !data?.integration && !oauthOpened && !edit) {
      nangoAuth('slack');
      setOauthOpened(true);
    }
  }, [appInstalled, data, nangoAuth, open, oauthOpened, edit]);

  const didGetAllChannels = slackData && !slackData.cursor;

  const showAuthenticationScreen =
    !appInstalled && !nangoSuccess && oauthOpened;

  return (
    <IntegrationDialog
      open={open}
      onClose={onClose}
      title={
        showAuthenticationScreen ? 'Authenticate Slack' : 'Connect your Slack'
      }
      onSubmit={handleSubmit((formData: SlackForm) => {
        onSubmit(formData);
      })}
      isValid={
        isValid &&
        !slackChannelsLoading &&
        !slackError &&
        !!watch('channelName')
      }
      hideSaveButton={showAuthenticationScreen || nangoError}
      error={error || slackIntegrationError}
      loading={loading}
      width={showAuthenticationScreen ? 398 : undefined}
      reset={() => {
        setOauthOpened(false);

        if (data?.integration || appInstalled) {
          getSlackChannels();
          reset({
            channelName: initialChannelName,
          });
        }
      }}
    >
      {showDeprecationWarning && (
        <Alert
          severity="warning"
          variant="filled"
          icon={alertIcons.info}
          style={{ fontWeight: 'bold' }}
        >
          Manual and private Slack apps are deprecated. Please{' '}
          <Link onClick={() => nangoAuth('slack')} href="#">
            connect
          </Link>{' '}
          Elementary Slack app
        </Alert>
      )}

      {nangoError && (
        <Alert severity="error" style={{ fontWeight: 'bold' }}>
          Elementary Slack app failed to connect, please{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>{' '}
          for further assistance.
        </Alert>
      )}

      {nangoSuccess && !slackIntegrationError && (
        <Alert severity="success" style={{ fontWeight: 'bold' }}>
          Elementary Slack app was successfully connected
          {slackIntegrationResponse?.newTeamName &&
            ` to ${slackIntegrationResponse.newTeamName}`}
        </Alert>
      )}

      {slackIntegrationResponse?.workspaceChanged && (
        <Alert severity="warning" style={{ fontWeight: 'bold' }}>
          Slack workspace has changed
          {slackIntegrationResponse.newTeamName &&
            ` to ${slackIntegrationResponse.newTeamName}`}
          . Please verify your channel destinations are still valid.
        </Alert>
      )}

      {oauthOpened && (
        <Typography>
          Follow the instructions on the authentication pop up, if any problem
          occurs please{' '}
          <Link onClick={supportChat.show} href="#">
            contact us
          </Link>
          .
        </Typography>
      )}

      {edit && !nangoSuccess && (appInstalled || data?.integration) && (
        <Button onClick={() => setShowEditConfirmationDialog(true)}>
          Re-authenticate your Slack integration
        </Button>
      )}

      {(appInstalled || nangoSuccess || data?.integration) && (
        <>
          {data?.teamName && !nangoSuccess && (
            <Typography variant="body2" color="text.secondary">
              Connected to workspace: <strong>{data.teamName}</strong>
            </Typography>
          )}

          <FormControls.InputWrapper>
            <Autocomplete
              options={slackData?.channels?.sort() ?? ['Loading channels...']}
              freeSolo={!didGetAllChannels}
              defaultValue={data?.defaultDestination?.channelName}
              onSelect={register('channelName').onChange}
              renderInput={(params) => (
                <TextField
                  label="Select default Slack channel"
                  {...params}
                  {...register('channelName', {
                    required: requiredLabel,
                  })}
                  size="large"
                />
              )}
            />
            <FormControls.ErrorMessage>
              {errors.channelName?.message}
            </FormControls.ErrorMessage>
          </FormControls.InputWrapper>

          {!didGetAllChannels && <ParietalChannelListAlert />}

          <MissingChannelHelper reloadOnClick={() => getSlackChannels()} />

          <TestIntegrationSection isSuccess={testSuccess}>
            <SlackTestSection
              channels={slackData?.channels}
              onSuccess={() => setTestSuccess(true)}
              onReset={() => setTestSuccess(false)}
            />
          </TestIntegrationSection>
        </>
      )}
      <EditConfirmationDialog
        open={showEditConfirmationDialog}
        onClose={() => setShowEditConfirmationDialog(false)}
        onConfirm={() => {
          setShowEditConfirmationDialog(false);
          setOauthOpened(true);
          nangoAuth('slack');
        }}
        message="This will overwrite the current Slack authentication details. Make sure the new credentials have the required permissions to avoid integration issues."
      />
      {edit && onDisconnect && (
        <Button
          variant="text"
          color="error"
          onClick={onDisconnect}
          sx={{ alignSelf: 'flex-start', mt: 1 }}
        >
          Disconnect Slack
        </Button>
      )}
    </IntegrationDialog>
  );
};

export default SlackDialog;
